(function () {
  "use strict";

  const ScrollAnimator = {
    config: {
      defaultThreshold: 0.1,
      defaultRootMargin: "0px 0px -50px 0px",
      defaultDelayMultiplier: 100,
      selector: ".animate-item",
      activeClass: "animate-in",
      defaultAnimationType: "fade-up",
      defaultAnimationDuration: 600,
      defaultAnimationEasing: "cubic-bezier(0.16, 1, 0.3, 1)",
      animationTypes: {
        fade: { opacity: 0 },
        "fade-up": { opacity: 0, transform: "translateY(40px)" },
        "fade-down": { opacity: 0, transform: "translateY(-40px)" },
        "fade-left": { opacity: 0, transform: "translateX(-40px)" },
        "fade-right": { opacity: 0, transform: "translateX(40px)" },
        "zoom-in": { opacity: 0, transform: "scale(0.9)" },
        "zoom-out": { opacity: 0, transform: "scale(1.1)" },
      },
    },

    state: {
      observer: null,
      initialized: false,
      elements: new Set(),
      isDisabled: false,
    },
    utils: {
      isElementInDOM: (element) => document.contains(element),

      getElementConfig: (element) => {
        return {
          delay: parseInt(element.dataset.delay) || 0,
          delayMultiplier:
            parseInt(element.dataset.delayMultiplier) ||
            ScrollAnimator.config.defaultDelayMultiplier,
          animationType:
            element.dataset.animationType ||
            ScrollAnimator.config.defaultAnimationType,
          animationDuration:
            parseInt(element.dataset.animationDuration) ||
            ScrollAnimator.config.defaultAnimationDuration,
          animationEasing:
            element.dataset.animationEasing ||
            ScrollAnimator.config.defaultAnimationEasing,
          threshold: parseFloat(element.dataset.threshold) || null,
          disabled: element.dataset.animationDisabled === "true",
        };
      },

      getObserverConfig: (element) => {
        const elementConfig = element
          ? ScrollAnimator.utils.getElementConfig(element)
          : {};
        return {
          threshold:
            elementConfig.threshold !== null
              ? elementConfig.threshold
              : parseFloat(document.documentElement.dataset.animateThreshold) ||
                ScrollAnimator.config.defaultThreshold,
          rootMargin:
            document.documentElement.dataset.animateRootMargin ||
            ScrollAnimator.config.defaultRootMargin,
        };
      },

      applyInitialStyles: (element) => {
        const { animationType, animationDuration, animationEasing, disabled } =
          ScrollAnimator.utils.getElementConfig(element);

        if (disabled || ScrollAnimator.state.isDisabled) {
          return;
        }

        let initialStyles = {
          transition: `opacity ${animationDuration}ms ${animationEasing}, transform ${animationDuration}ms ${animationEasing}`,
          willChange: "opacity, transform",
        };

        if (ScrollAnimator.config.animationTypes[animationType]) {
          Object.assign(
            initialStyles,
            ScrollAnimator.config.animationTypes[animationType]
          );
        } else if (animationType === "custom") {
          // Özel animasyon için bir şey yapma, CSS tarafından kontrol edilecek
        } else {
          Object.assign(
            initialStyles,
            ScrollAnimator.config.animationTypes[
              ScrollAnimator.config.defaultAnimationType
            ]
          );
        }

        Object.assign(element.style, initialStyles);
      },

      resetAnimationStyles: (element) => {
        element.style.opacity = "";
        element.style.transform = "";

        setTimeout(() => {
          element.style.transition = "";
          element.style.willChange = "";
        }, ScrollAnimator.utils.getElementConfig(element).animationDuration + 50);
      },

      animateAllImmediately: (selector = null) => {
        const elementsToAnimate = selector
          ? document.querySelectorAll(selector)
          : document.querySelectorAll(ScrollAnimator.config.selector);

        elementsToAnimate.forEach((element) => {
          if (!element.classList.contains(ScrollAnimator.config.activeClass)) {
            const { disabled } = ScrollAnimator.utils.getElementConfig(element);
            if (disabled || ScrollAnimator.state.isDisabled) return;
            ScrollAnimator.utils.applyInitialStyles(element);
            element.classList.add(ScrollAnimator.config.activeClass);
            ScrollAnimator.utils.resetAnimationStyles(element);
          }
        });
      },
    },
    handleIntersection: (entries, observer) => {
      if (ScrollAnimator.state.isDisabled) return;

      entries.forEach((entry) => {
        const element = entry.target;
        const { delay, delayMultiplier } =
          ScrollAnimator.utils.getElementConfig(element);

        if (entry.isIntersecting) {
          if (!element.classList.contains(ScrollAnimator.config.activeClass)) {
            if (element._animationTimeout) {
              clearTimeout(element._animationTimeout);
            }
            element._animationTimeout = setTimeout(() => {
              if (ScrollAnimator.utils.isElementInDOM(element)) {
                element.classList.add(ScrollAnimator.config.activeClass);
                ScrollAnimator.utils.resetAnimationStyles(element);
              }
              element._animationTimeout = null;
            }, delay * delayMultiplier);
          }
        } else {
          if (element._animationTimeout) {
            clearTimeout(element._animationTimeout);
            element._animationTimeout = null;
          }
          if (element.classList.contains(ScrollAnimator.config.activeClass)) {
            element.classList.remove(ScrollAnimator.config.activeClass);
            ScrollAnimator.utils.applyInitialStyles(element);
          }
        }
      });
    },
    createObserver: (element = null) => {
      if (!window.IntersectionObserver) {
        ScrollAnimator.fallbackInit();
        return null;
      }

      return new IntersectionObserver(
        ScrollAnimator.handleIntersection,
        ScrollAnimator.utils.getObserverConfig(element)
      );
    },
    fallbackInit: () => {
      const elements = document.querySelectorAll(
        ScrollAnimator.config.selector
      );
      elements.forEach((element) => {
        const { disabled } = ScrollAnimator.utils.getElementConfig(element);
        if (disabled || ScrollAnimator.state.isDisabled) {
          element.classList.add(ScrollAnimator.config.activeClass);
          return;
        }
        ScrollAnimator.utils.applyInitialStyles(element);
        element.classList.add(ScrollAnimator.config.activeClass);
        ScrollAnimator.utils.resetAnimationStyles(element);
      });
    },
    observeElements: (elements) => {
      if (!ScrollAnimator.state.observer && window.IntersectionObserver) {
        ScrollAnimator.state.observer = ScrollAnimator.createObserver();
        if (
          !ScrollAnimator.state.observer &&
          !ScrollAnimator.state.initialized
        ) {
          ScrollAnimator.fallbackInit();
          ScrollAnimator.state.initialized = true;
          return;
        }
      } else if (!ScrollAnimator.state.observer) {
        if (!ScrollAnimator.state.initialized) {
          ScrollAnimator.fallbackInit();
          ScrollAnimator.state.initialized = true;
        }
        return;
      }

      elements.forEach((element) => {
        if (!ScrollAnimator.state.elements.has(element)) {
          const { disabled } = ScrollAnimator.utils.getElementConfig(element);
          if (disabled) {
            return;
          }

          ScrollAnimator.utils.applyInitialStyles(element);

          if (
            ScrollAnimator.isElementInViewport(element) &&
            !element.classList.contains(ScrollAnimator.config.activeClass)
          ) {
            const { delay, delayMultiplier } =
              ScrollAnimator.utils.getElementConfig(element);
            setTimeout(() => {
              if (ScrollAnimator.utils.isElementInDOM(element)) {
                element.classList.add(ScrollAnimator.config.activeClass);
                ScrollAnimator.utils.resetAnimationStyles(element);
              }
            }, delay * delayMultiplier);
          }
          ScrollAnimator.state.observer.observe(element);
          ScrollAnimator.state.elements.add(element);
        }
      });
    },
    isElementInViewport: (element) => {
      const rect = element.getBoundingClientRect();
      return (
        rect.top < window.innerHeight &&
        rect.bottom >= 0 &&
        rect.left < window.innerWidth &&
        rect.right >= 0
      );
    },
    scanForElements: () => {
      if (ScrollAnimator.state.isDisabled) {
        ScrollAnimator.utils.animateAllImmediately();
        return 0;
      }

      const elements = document.querySelectorAll(
        ScrollAnimator.config.selector
      );
      const newElements = Array.from(elements).filter(
        (element) => !ScrollAnimator.state.elements.has(element)
      );

      if (newElements.length > 0) {
        ScrollAnimator.observeElements(newElements);
      }

      return newElements.length;
    },
    init: () => {
      if (ScrollAnimator.state.initialized && ScrollAnimator.state.observer)
        return;

      const disableAnimationsGlobally =
        document.documentElement.dataset.animationDisabled === "true";
      ScrollAnimator.state.isDisabled = disableAnimationsGlobally;

      if (ScrollAnimator.state.isDisabled) {
        ScrollAnimator.utils.animateAllImmediately();
        ScrollAnimator.state.initialized = true;
        return;
      }

      ScrollAnimator.state.observer = ScrollAnimator.createObserver();
      if (ScrollAnimator.state.observer) {
        ScrollAnimator.state.initialized = true;
      }

      ScrollAnimator.scanForElements();

      if (window.MutationObserver) {
        const mutationObserver = new MutationObserver((mutations) => {
          let shouldScan = false;
          mutations.forEach((mutation) => {
            if (
              mutation.type === "childList" &&
              mutation.addedNodes.length > 0
            ) {
              mutation.addedNodes.forEach((node) => {
                if (
                  node.nodeType === 1 &&
                  (node.matches(ScrollAnimator.config.selector) ||
                    node.querySelector(ScrollAnimator.config.selector))
                ) {
                  shouldScan = true;
                }
              });
            }
          });

          if (shouldScan) {
            clearTimeout(ScrollAnimator._scanTimeout);
            ScrollAnimator._scanTimeout = setTimeout(() => {
              ScrollAnimator.scanForElements();
            }, 100);
          }
        });

        mutationObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });
      } else if (!ScrollAnimator.state.initialized) {
        ScrollAnimator.fallbackInit();
        ScrollAnimator.state.initialized = true;
      }
    },
    refresh: () => {
      ScrollAnimator.scanForElements();
    },
    disable: () => {
      ScrollAnimator.state.isDisabled = true;
      ScrollAnimator.state.elements.forEach((element) => {
        if (ScrollAnimator.state.observer)
          ScrollAnimator.state.observer.unobserve(element);
        if (element._animationTimeout) clearTimeout(element._animationTimeout);
        ScrollAnimator.utils.applyInitialStyles(element);
        element.classList.add(ScrollAnimator.config.activeClass);
        element.style.opacity = "";
        element.style.transform = "";
        element.style.transition = "none";
      });
      ScrollAnimator.state.elements.clear();
      ScrollAnimator.utils.animateAllImmediately();
    },
    enable: () => {
      ScrollAnimator.state.isDisabled = false;
      document
        .querySelectorAll(ScrollAnimator.config.selector)
        .forEach((el) => {
          el.classList.remove(ScrollAnimator.config.activeClass);
          ScrollAnimator.utils.applyInitialStyles(el);
        });
      ScrollAnimator.state.elements.clear();
      if (ScrollAnimator.state.observer) {
        ScrollAnimator.scanForElements();
      } else {
        ScrollAnimator.init();
      }
    },
    destroy: () => {
      if (ScrollAnimator.state.observer) {
        ScrollAnimator.state.observer.disconnect();
        ScrollAnimator.state.observer = null;
      }

      document
        .querySelectorAll(ScrollAnimator.config.selector)
        .forEach((element) => {
          if (element._animationTimeout) {
            clearTimeout(element._animationTimeout);
          }
          element.classList.remove(ScrollAnimator.config.activeClass);
          element.style.opacity = "";
          element.style.transform = "";
          element.style.transition = "";
          element.style.willChange = "";
        });

      ScrollAnimator.state.elements.clear();
      ScrollAnimator.state.initialized = false;
      ScrollAnimator.state.isDisabled = false;
    },
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", ScrollAnimator.init);
  } else {
    ScrollAnimator.init();
  }

  window.ScrollAnimator = {
    refresh: ScrollAnimator.refresh,
    destroy: ScrollAnimator.destroy,
    init: ScrollAnimator.init,
    disable: ScrollAnimator.disable,
    enable: ScrollAnimator.enable,
    animateAllImmediately: ScrollAnimator.utils.animateAllImmediately,
  };

  function ordinalSuffix(day) {
    if (day > 3 && day < 21) return day + "th";
    switch (day % 10) {
      case 1:
        return day + "st";
      case 2:
        return day + "nd";
      case 3:
        return day + "rd";
      default:
        return day + "th";
    }
  }

  function initFastDelivery() {
    const wrapper = document.getElementById("fast-delivery-wrapper");
    if (!wrapper) return;

    const timezone =
      wrapper.dataset.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    const cutoffTime = wrapper.dataset.cutoffTime || "21:00";
    const [h, m] = cutoffTime.split(":").map(Number);

    function updateCountdown() {
      const now = new Date();
      let cutoff = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        h,
        m,
        0
      );

      if (now >= cutoff) cutoff.setDate(cutoff.getDate() + 1);

      const diff = Math.floor((cutoff - now) / 1000);
      const hrs = Math.floor(diff / 3600);
      const mins = Math.floor((diff % 3600) / 60);
      const secs = diff % 60;

      document.getElementById("delivery-countdown").textContent = `${
        hrs < 10 ? "0" + hrs : hrs
      }h ${mins < 10 ? "0" + mins : mins}m ${secs < 10 ? "0" + secs : secs}s`;

      const opts = { weekday: "short", month: "short", timeZone: timezone };
      const weekday = cutoff.toLocaleDateString("en-US", opts);
      const month = cutoff.toLocaleDateString("en-US", opts).split(" ")[1];
      const day = cutoff.getDate();
      document.getElementById(
        "delivery-date"
      ).textContent = `${weekday}, ${month}. ${ordinalSuffix(day)}`;

      setTimeout(updateCountdown, 1000);
    }

    updateCountdown();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFastDelivery);
  } else {
    initFastDelivery();
  }
})();
